<template>
  <span
    class="m-icon"
    :class="{
      [`m-icon--${size}`]: size,
      [`m-icon--${color}`]: color,
      'm-icon--block': block,
      'm-icon--badge': badge,
    }"
    :aria-hidden="!title"
  >
    <InlineSvg
      v-if="src"
      :key="key"
      role="img"
      :src="src"
      :title="title"
      :aria-label="title"
    />
  </span>
</template>

<script>
import feather from 'feather-icons'
import { v4 as uuidv4 } from 'uuid'
import InlineSvg from 'vue-inline-svg'

import customIcons from './custom-icons'

const iconPropValidator = (prop) => {
  const icons = [...Object.keys(feather.icons), ...Object.keys(customIcons)]
  const isValid = icons.includes(prop)

  if (!isValid) {
    console.error('Invalid icon prop. Available icons are: ', icons)
  }

  return isValid
}

export default {
  name: 'MIcon',
  components: { InlineSvg },
  props: {
    /** Icon to display.  */
    icon: {
      type: String,
      required: true,
      validator: iconPropValidator,
    },
    /** Aria title for screen readers.  */
    title: {
      type: String,
      default: null,
    },
    /** Font size to use.  */
    size: {
      type: String,
      default: undefined,
      validator: (prop) =>
        [
          'base',
          'sm',
          'lg',
          'h1',
          'h2',
          'h3',
          'h4',
          'h5',
          'display1',
          'display2',
        ].includes(prop),
    },
    /** Color of the icon  */
    color: {
      type: String,
      default: undefined,
      validator: (prop) =>
        ['default', 'info', 'success', 'warning', 'error', 'disabled'].includes(
          prop
        ),
    },
    /**
     * Make the icon display block to remove the invisible 3px border added by browsers.
     */
    block: {
      type: Boolean,
      default: false,
    },
    /**
     * Display the icon inside a badge circle.
     */
    badge: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // Using icon as key would make sense, but it's causing reactivity issues.
      // Using "src" is overly long and not always guaranteed to be a string because of async, so we use a key state.
      key: null,
      src: '',
    }
  },
  watch: {
    icon: {
      handler() {
        this.iconLoader()
      },
      immediate: true,
    },
  },
  methods: {
    async iconLoader() {
      const { icon } = this
      let src
      /**
       * If we have a custom icon grab it from our assets folder,
       * otherwise grab from feather icons package
       */
      if (customIcons[icon]) {
        src = await import(
          /* webpackChunkName: "custom-icons" */
          /* webpackMode: "lazy" */
          `../../../assets/icons/${icon}.svg`
        ).then((module) => module.default || module)
      } else {
        src = await import(
          /* webpackChunkName: "feather-icons" */
          /* webpackMode: "lazy" */
          `feather-icons/dist/icons/${icon}.svg`
        ).then((module) => module.default || module)
      }

      this.src = src
      this.key = uuidv4()
    },
  },
}
</script>

<style lang="scss" scoped>
$icon-sizes: (
  sm: var(--typography-paragraph-small-font-size),
  base: var(--typography-paragraph-regular-font-size),
  lg: var(--typography-paragraph-large-font-size),
  h5: var(--typography-h5-font-size),
  h4: var(--typography-h4-font-size),
  h3: var(--typography-h3-font-size),
  h2: var(--typography-h2-font-size),
  h1: var(--typography-h1-font-size),
  display1: var(--typography-h1-display-font-size),
  display2: var(--typography-h2-display-font-size),
);

$icon-colors: info, success, warning, error, 'disabled';

.m-icon {
  align-items: center;
  display: inline-flex;
  height: 1em;
  justify-content: center;
  line-height: 1;
  width: 1em;

  :deep(svg) {
    display: block;
    height: 1em;
    width: 1em;
  }

  &--block {
    display: flex;
  }

  @each $size, $value in $icon-sizes {
    &--#{$size} {
      font-size: $value;
    }
  }

  @each $color in $icon-colors {
    &--#{$color} {
      color: var(--state-#{$color}-dark);
    }
  }

  &--badge {
    background: var(--skin-background-hover);
    border-radius: 50%;
    height: 2em;
    width: 2em;

    @each $color in $icon-colors {
      &.m-icon--#{$color} {
        background: var(--state-#{$color}-light);
      }
    }
  }
}
</style>
